import React, { useState, useEffect, useCallback } from 'react';
import AnalyticsContext, {
  AnalyticsContextInterface,
  AnalyticsEvent,
} from './AnalyticsContext';
import { Props } from './AnalyticsProvider.d';
import useUser from '@/hooks/useUser';
import { isAnalyticsSuppressed } from '@/utils/cookies';
import * as analyticsClient from './analyticsClient';

const AnalyticsProvider = ({ children }: Props): JSX.Element => {
  const { is_subscriber, isPending } = useUser();
  const [delayedEvents, setDelayedEvents] = useState([]);

  const track = useCallback(
    (event: AnalyticsEvent, properties) => {
      const defaultProperties = {
        app_name: 'React App',
        is_subscriber: Boolean(is_subscriber),
        path: window.location.pathname,
        url: window.location.href,
      };

      const eventProperties = {
        ...properties,
        ...defaultProperties,
      };

      // extend event properties with new props
      switch (event) {
        case AnalyticsEvent.PRODUCT_CATEGORY_VIEWED:
        case AnalyticsEvent.PRODUCT_CLICKED:
          eventProperties.app = 'rui';
          eventProperties.content_group = 'category page';
          break;

        case AnalyticsEvent.PRIMARY_NAVIGATION_CLICK:
          eventProperties.category = 'Navigation';
          break;

        case AnalyticsEvent.SUPER_HERO_CONTENT_BLOCK_CLICK:
          eventProperties.catalog = 'Home Page';
          break;

        case AnalyticsEvent.CONTINUE_WITHOUT_OFFER:
          eventProperties.category = 'Exit Intent Modal';
          break;
      }

      if (isPending) {
        return setDelayedEvents(delayedEvents => [
          ...delayedEvents,
          { name: event, eventProperties },
        ]);
      }

      if (analyticsClient && !isAnalyticsSuppressed()) {
        analyticsClient.track(event, eventProperties);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isPending]
  );

  const page = useCallback(
    (event, properties) => {
      const eventProperties = {
        ...properties,
        app_name: 'React App',
        is_subscriber: Boolean(is_subscriber),
      };

      if (isPending) {
        return setDelayedEvents(delayedEvents => [
          ...delayedEvents,
          { page: event, eventProperties },
        ]);
      }

      if (analyticsClient && !isAnalyticsSuppressed()) {
        analyticsClient.page(event, eventProperties);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isPending]
  );

  useEffect(() => {
    if (!isPending && delayedEvents.length) {
      delayedEvents.forEach(event => {
        if (event.name) track(event.name, event.eventProperties);
        if (event.page) page(event.page, event.eventProperties);
      });
      setDelayedEvents([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delayedEvents.length, isPending]);

  const context: AnalyticsContextInterface = {
    track,
    page,
  };

  return (
    <AnalyticsContext.Provider value={context}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export default AnalyticsProvider;
